/**
 * CopyLayoutPopover — copy the locked wall's layout to the room's other walls.
 *
 * Pick the walls (or all of them), say which kinds of opening come along
 * (none by default), press Copy. What was on the chosen walls is replaced; the
 * copy is one undoable edit. The rules of the copy itself live in
 * calculations/sections/copyLayout.ts.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Surface } from '../../types/sections';
import { CopyLayoutOptions } from '../../calculations/sections/copyLayout';
import { DESIGNER_STRINGS as S } from './strings';

interface Props {
  /** The wall being edited. */
  source: Surface;
  /** The room's other walls, in display order. */
  others: Surface[];
  onCopy: (targetIds: string[], openings: NonNullable<CopyLayoutOptions['openings']>) => void;
  buttonStyle: React.CSSProperties;
  panelStyle: React.CSSProperties;
}

const OPENING_KINDS = ['door', 'window', 'garageDoor'] as const;

export const CopyLayoutPopover: React.FC<Props> = ({ source, others, onCopy, buttonStyle, panelStyle }) => {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<Set<string>>(() => new Set());
  const [kinds, setKinds] = useState<Record<string, boolean>>({ door: false, window: false, garageDoor: false });
  const rootRef = useRef<HTMLDivElement>(null);

  // Closes on a click outside, or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('pointerdown', onDown, true); window.removeEventListener('keydown', onKey, true); };
  }, [open]);

  // A different source wall: start afresh (the targets were for the last one).
  useEffect(() => { setTargets(new Set()); }, [source.id]);

  const all = others.length > 0 && others.every((w) => targets.has(w.id));
  const toggle = (id: string) => setTargets((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleAll = () => setTargets(all ? new Set() : new Set(others.map((w) => w.id)));
  const canCopy = targets.size > 0 && source.sections.length + (source.openings?.length ?? 0) > 0;

  const check = (checked: boolean, onChange: () => void, label: string, key: string) => (
    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', padding: '3px 0' }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ accentColor: '#FEB100' }} />
      {label}
    </label>
  );

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex' }}>
      <button
        type="button"
        title={S.copyLayout}
        aria-label={S.copyLayout}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ ...buttonStyle, width: 34, height: 30, ...(open ? { background: '#FEB100', color: '#000', border: '1px solid #FEB100' } : {}) }}
      >
        <Copy size={15} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={S.copyLayout}
          onPointerDown={(e) => e.stopPropagation()}
          style={{ ...panelStyle, position: 'absolute', left: 0, top: 36, width: 250, padding: '10px 12px', borderRadius: 10, zIndex: 6 }}
        >
          <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13 }}>{S.copyLayout}</p>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: '#b9b9c4', lineHeight: 1.4 }}>{S.copyLayoutHint(source.name)}</p>
          <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 600, color: '#cfcfd6', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{S.copyTo}</p>
          {check(all, toggleAll, S.copyToAll, 'all')}
          <div style={{ paddingLeft: 10 }}>
            {others.map((w) => check(targets.has(w.id), () => toggle(w.id), w.name, w.id))}
          </div>
          <p style={{ margin: '8px 0 2px', fontSize: 11, fontWeight: 600, color: '#cfcfd6', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{S.copyInclude}</p>
          {OPENING_KINDS.map((k) => check(!!kinds[k], () => setKinds((c) => ({ ...c, [k]: !c[k] })), S.copyIncludeKind(S.openingName[k]), k))}
          <p style={{ margin: '8px 0 6px', fontSize: 11, color: '#ffb4a8', lineHeight: 1.4 }}>{S.copyWarning}</p>
          <button
            type="button"
            disabled={!canCopy}
            onClick={() => { onCopy([...targets], { door: !!kinds.door, window: !!kinds.window, garageDoor: !!kinds.garageDoor }); setOpen(false); }}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 12, cursor: canCopy ? 'pointer' : 'default', background: canCopy ? '#FEB100' : 'rgba(255,255,255,0.12)', color: canCopy ? '#000' : '#8a8a94' }}
          >
            {S.copyNow(targets.size)}
          </button>
        </div>
      )}
    </div>
  );
};

export default CopyLayoutPopover;
